import { Directive, ElementRef, Input, OnChanges, Renderer2 } from '@angular/core';
import { IStandardTranscriptWord } from '../../../../../shared/interfaces/standard-transcript.interface';

type TParagraphData = {
  words: IStandardTranscriptWord[];
  paragraph: string;
};
const NEGATIVE_INDEX = -1;

@Directive({
  selector: '[cvRenderParagraph]',
  standalone: true,
})
export class RenderParagraphDirective implements OnChanges {
  @Input({ required: true, transform: parseParagraphData }) paragraphData: TParagraphData = {
    words: [],
    paragraph: '',
  };
  @Input({ required: true }) currentAudioTime: number = 0;

  private hasScrolledIntoView: boolean = false;
  private lastSpokenWordIndex: number = NEGATIVE_INDEX;

  constructor(
    private readonly elementRef: ElementRef<HTMLDivElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnChanges(): void {
    if (this.isActiveParagraph) {
      this.displayHighlightedParagraph();

      if (!this.hasScrolledIntoView) {
        this.scrollIntoView();
        this.hasScrolledIntoView = true;
      }
    } else {
      this.displayPlainParagraph();
      this.hasScrolledIntoView = false;
      this.lastSpokenWordIndex = NEGATIVE_INDEX;
    }
  }

  private get isActiveParagraph(): boolean {
    const firstWordStartTime = this.paragraphData.words[0].start;
    const lastWordEndTime = this.paragraphData.words[this.paragraphData.words.length - 1].end;

    return (
      this.currentAudioTime !== 0 &&
      this.currentAudioTime >= firstWordStartTime &&
      this.currentAudioTime <= lastWordEndTime
    );
  }

  private displayPlainParagraph(): void {
    if (this.elementRef.nativeElement.innerHTML !== this.paragraphData.paragraph) {
      this.renderer.setProperty(this.elementRef.nativeElement, 'innerHTML', this.paragraphData.paragraph);
    }
  }

  private displayHighlightedParagraph(): void {
    const currentlySpokenWordIndex = this.paragraphData.words.findIndex(
      ({ start, end }) => this.currentAudioTime >= start && this.currentAudioTime <= end,
    );

    if (currentlySpokenWordIndex < 0 || this.lastSpokenWordIndex === currentlySpokenWordIndex) {
      return;
    }

    this.lastSpokenWordIndex = currentlySpokenWordIndex;
    this.renderer.setProperty(
      this.elementRef.nativeElement,
      'innerHTML',
      this.createHighlightedParagraph(currentlySpokenWordIndex),
    );
  }

  private createHighlightedParagraph(activeWordIndex: number): string {
    const activeWord = this.paragraphData.words[activeWordIndex].punctuatedWord;
    const highlightedWords = this.getPhraseByIndexes(0, activeWordIndex);
    const remainingWords = this.getPhraseByIndexes(activeWordIndex + 1);

    return `<span class="highlighted-words">${highlightedWords} <span class="active-word">${activeWord}</span></span> ${remainingWords}`;
  }

  private getPhraseByIndexes(startIndex: number, endIndex?: number): string {
    return this.paragraphData.words
      .slice(startIndex, endIndex)
      .map((word) => word.punctuatedWord)
      .join(' ');
  }

  private scrollIntoView(): void {
    this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function parseParagraphData(words: IStandardTranscriptWord[]): TParagraphData {
  return { words, paragraph: words.map(({ punctuatedWord }) => punctuatedWord).join(' ') };
}
